import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';

import css from './SearchFilterDrawer.module.css';

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <line x1="6" y1="6" x2="18" y2="18" />
    <line x1="18" y1="6" x2="6" y2="18" />
  </svg>
);

const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/**
 * SearchFilterDrawer — the single maroon filter panel used at every viewport
 * width. Slides in from the left over a dimmed backdrop. Filters render as
 * children (FilterComponent list with liveEdit, so checking a box applies
 * immediately); the footer "Show N horses" button just closes the drawer.
 *
 * Open/close + onManageDisableScrolling wiring mirrors SearchFiltersMobile;
 * Escape, backdrop click, and the X all close it, and focus is trapped inside
 * while open (focus returns to the Filters toggle via the parent's onClose).
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the drawer is open
 * @param {Function} props.onClose - Close the drawer (parent restores focus to the toggle)
 * @param {Function} props.onClearAll - Reset all filters incl. keywords
 * @param {number} props.resultsCount - Live totalItems for the footer button
 * @param {Function} props.onManageDisableScrolling - Body scroll lock
 * @param {React.Node} props.children - FilterComponent list
 * @returns {JSX.Element}
 */
const SearchFilterDrawer = props => {
  const intl = useIntl();
  const { isOpen, onClose, onClearAll, resultsCount, onManageDisableScrolling, children } = props;
  const drawerRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    onManageDisableScrolling('SearchFilterDrawer', isOpen);
    return () => onManageDisableScrolling('SearchFilterDrawer', false);
  }, [isOpen, onManageDisableScrolling]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    closeButtonRef.current?.focus();

    const handleKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !drawerRef.current) {
        return;
      }
      const focusable = Array.from(drawerRef.current.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        el => !el.disabled && el.offsetParent !== null
      );
      if (focusable.length === 0) {
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (event.shiftKey && (active === first || !drawerRef.current.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !drawerRef.current.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const inertMaybe = isOpen ? {} : { inert: '' };

  return (
    <div className={classNames(css.root, { [css.isOpen]: isOpen })} aria-hidden={!isOpen}>
      <div className={css.backdrop} onClick={onClose} />
      <div
        className={css.drawer}
        ref={drawerRef}
        role="dialog"
        aria-modal="true"
        aria-label={intl.formatMessage({ id: 'SearchFilterDrawer.heading' })}
        {...inertMaybe}
      >
        <div className={css.header}>
          <span className={css.heading}>
            <FormattedMessage id="SearchFilterDrawer.heading" />
          </span>
          <button
            type="button"
            ref={closeButtonRef}
            className={css.closeButton}
            onClick={onClose}
            aria-label={intl.formatMessage({ id: 'SearchFilterDrawer.closeDrawer' })}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={css.filtersScroll}>{children}</div>
        <div className={css.footer}>
          <button type="button" className={css.clearButton} onClick={onClearAll}>
            <FormattedMessage id="SearchFilterDrawer.clearAll" />
          </button>
          <button type="button" className={css.applyButton} onClick={onClose}>
            <FormattedMessage id="SearchFilterDrawer.showResults" values={{ count: resultsCount }} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SearchFilterDrawer;
